import { IDisposable } from "../../../base/common/lifecycle.js";
import { URI } from "../../../base/common/uri.js";
import { createFileSystemProviderError, FileSystemProviderCapabilities, FileSystemProviderErrorCode, FileType, IFileChange, IFileDeleteOptions, IFileOverwriteOptions, IFileSystemProvider, IStat, IWatchOptions } from "../common/files.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { VSBuffer } from "../../../base/common/buffer.js";
import { localize } from "../../../base/common/nls.js";
import { LogService } from "../../log/common/logService.js";
import { ILogService } from "../../log/common/log.js";



export abstract class BaseBrowserStorageFileSystemProvider implements IFileSystemProvider {

    readonly capabilities = FileSystemProviderCapabilities.FileReadWrite;

    private readonly _onDidChangeCapabilities = new Emitter<void>();
    readonly onDidChangeCapabilities = this._onDidChangeCapabilities.event;

    private readonly _onDidChangeFile = new Emitter<readonly IFileChange[]>();
    readonly onDidChangeFile = this._onDidChangeFile.event;

    // readonly onDidWatchError?: Event<string>;

    constructor(
        @ILogService private readonly logger: LogService
    ) {

    }

    abstract get storage(): Storage;

    watch(resource: URI, opts: IWatchOptions): IDisposable {
        console.error('[BrowserStorage] watch not implemented');
        return { dispose() { } };
    }

    async stat(resource: URI): Promise<IStat> {
        if (!resource.path) {
            return {
                type: FileType.Directory,
                ctime: 0,
                mtime: 0,
                size: 0
            };
        }
        const item = this.storage.getItem(resource.path);
        // console.log('STAT', resource.path, item);
        if (item) {
            return {
                type: FileType.File,
                ctime: 0,
                mtime: 0,
                size: item.length
            };
        }
        return Promise.reject(new Error('File not found'));
    }

    async mkdir(_resource: URI): Promise<void> {
        // no-op, as localStorage does not have directories
    }

    readdir(resource: URI): Promise<[string, FileType][]> {
        const entries: [string, FileType][] = [];
        const prefix = resource.path?.endsWith('/') ? resource.path : resource.path + '/';
        const seen = new Set<string>();

        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);
            if (key && key.startsWith(prefix)) {
                const relativePath = key.substring(prefix.length);
                const parts = relativePath.split('/');
                const entryName = parts[0];
                if (!seen.has(entryName)) {
                    seen.add(entryName);
                    const isDirectory = parts.length > 1;
                    entries.push([entryName, isDirectory ? FileType.Directory : FileType.File]);
                }
            }
        }
        return Promise.resolve(entries);
    }

    async delete(resource: URI, opts: IFileDeleteOptions): Promise<void> {
        if (!resource.path) {
            return;
        }
        if (opts.atomic) {
            this.logger.error('[BrowserStorage] atomic delete not implemented');
        }
        this.storage.removeItem(resource.path);
    }

    async rename(from: URI, to: URI, opts: IFileOverwriteOptions): Promise<void> {
        if (!from.path || !to.path) {
            this.logger.error('[BrowserStorage] invalid paths for rename');
            return;
        }
        this.logger.debug('RENAME FILE', from.path, '->', to.path);
        const item = this.storage.getItem(from.path);
        if (item) {
            const override = opts.overwrite;
            if (!override) {
                const existing = this.storage.getItem(to.path);
                if (existing) {
                    return Promise.reject(new Error('File already exists'));
                }
            }
            this.storage.setItem(to.path, item);
            this.storage.removeItem(from.path);
        } else {
            return Promise.reject(new Error('File not found'));
        }
    }

    async readFile(resource: URI): Promise<Uint8Array> {
        if (!resource.path) {
            return Promise.reject(new Error('Invalid resource path'));
        }
        this.logger.debug('READ FILE', resource.path);
        const item = this.storage.getItem(resource.path);
        if (item) {
            const buffer = VSBuffer.fromString(item);
            return buffer.buffer;
        }

        throw createFileSystemProviderError(localize("fileNotExists", `File not found: ${resource.toString()}`), FileSystemProviderErrorCode.FileNotFound);
    }

    writeFile(resource: URI, content: Uint8Array): Promise<void> {
        if (!resource.path) {
            return Promise.reject(new Error('Invalid resource path'));
        }
        this.logger.debug('WRITE FILE', resource.path);
        const data = VSBuffer.wrap(content).toString();
        this.storage.setItem(resource.path, data);
        return Promise.resolve();
    }

    // save(key: string, data: any): void {
    //     this.storage.setItem(key, JSON.stringify(data));
    // }

    // load<T>(key: string): T | null {
    //     const item = this.storage.getItem(key);
    //     if (item) {
    //         return JSON.parse(item) as T;
    //     }
    //     return null;
    // }
}


export class LocalStorageFileSystemProvider extends BaseBrowserStorageFileSystemProvider {
    get storage() {
        return window.localStorage;
    }
}

export class SessionStorageFileSystemProvider extends BaseBrowserStorageFileSystemProvider {
    get storage() {
        return window.sessionStorage;
    }
}